/* ============================================================================
   Bouchon — custom.js
   Loaded after theme.js. Contains:
     - <quick-order-search>  type-to-search SKU autocomplete + draft cart
     - <sample-picker>        type-to-search picker that builds an email payload
     - <csv-upload>           CSV parser + validator + cart-adder
   All use Shopify's predictive search (/search/suggest.json) to resolve SKUs.
   Cart adds use /cart/add.js.
   ============================================================================ */

(function() {
  'use strict';

  function bindVendorDirectory(root) {
    if (!root || root.dataset.vendorDirectoryBound === 'true') return;

    const input = root.querySelector('[data-vendor-search]');
    const cards = Array.from(root.querySelectorAll('[data-vendor-card]'));
    const sections = Array.from(root.querySelectorAll('[data-vendor-letter-section]'));
    const empty = root.querySelector('[data-vendor-empty]');

    if (!input || !cards.length) return;

    root.dataset.vendorDirectoryBound = 'true';
    input.addEventListener('input', () => {
      const query = input.value.trim().toLowerCase();
      let visibleCards = 0;

      cards.forEach((card) => {
        const visible = !query || (card.dataset.vendorName || '').includes(query);
        card.hidden = !visible;
        if (visible) visibleCards += 1;
      });

      sections.forEach((section) => {
        section.hidden = !section.querySelector('[data-vendor-card]:not([hidden])');
      });

      if (empty) empty.hidden = visibleCards > 0;
    });
  }

  function installProducerPageFallback() {
    const path = window.location.pathname.replace(/\/+$/, '');
    if (path !== '/pages/producers') return;
    if (new URLSearchParams(window.location.search).has('view')) return;

    const existing = document.querySelector('[data-section-type="producer-vendor-index"]');
    if (existing) {
      bindVendorDirectory(existing);
      return;
    }

    fetch('/pages/producers?view=producer-index&_bx_vendor_index=' + Date.now(), {
      credentials: 'same-origin',
      headers: { 'Accept': 'text/html' }
    })
      .then((response) => response.ok ? response.text() : '')
      .then((html) => {
        if (!html || html.indexOf('producer-vendor-index') === -1) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const nextMain = doc.querySelector('main#main');
        const currentMain = document.querySelector('main#main');
        if (!nextMain || !currentMain) return;

        currentMain.innerHTML = nextMain.innerHTML;
        bindVendorDirectory(currentMain.querySelector('[data-section-type="producer-vendor-index"]'));
      })
      .catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installProducerPageFallback);
  } else {
    installProducerPageFallback();
  }

  // ── Shared helpers ─────────────────────────────────────────────────────────

  const moneyFormat = (window.theme && window.theme.moneyFormat) || '${{amount}}';

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    const dollars = (cents / 100).toFixed(2);
    return moneyFormat
      .replace('{{amount}}', dollars)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', dollars.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
  }

  // AU trade pricing — Shopify stores prices RAW ex-GST (tax-inclusive mode OFF).
  // Spirits/liqueurs: stored = ex-GST, display as-is.
  // Legacy non-taxable wines still need WET added until their catalog price is migrated.
  // Label always "ex GST" per AU trade convention.
  function isWineProduct(p) {
    if (!p) return false;
    const t = p.type || p.product_type || '';
    if (typeof t === 'string' && t.indexOf('Wine') === 0) return true;
    const tags = p.tags;
    if (Array.isArray(tags)) {
      if (tags.indexOf('spirit:wine') !== -1) return true;
    } else if (typeof tags === 'string') {
      if (tags.split(',').map(s => s.trim()).indexOf('spirit:wine') !== -1) return true;
    }
    return false;
  }
  function priceExGstCents(cents, product, variant) {
    const mult = isWineProduct(product) && variant && variant.taxable === false ? 1.29 : 1;
    return Math.round((cents || 0) * mult);
  }

  // Predictive search via Shopify's storefront API. Returns up to N products.
  async function searchProducts(query, limit) {
    if (!query || query.length < 2) return [];
    limit = limit || 6;
    const url = (window.routes.searchUrl || '/search') + '/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=' + limit + '&resources[options][unavailable_products]=last';
    try {
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      const data = await res.json();
      return (data && data.resources && data.resources.results && data.resources.results.products) || [];
    } catch (err) {
      console.warn('Bouchon search failed:', err);
      return [];
    }
  }

  async function fetchProductByHandle(handle) {
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  }

  async function cartAddItems(items) {
    const res = await fetch(window.routes.cartAddUrl || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Cart add failed');
    }
    return await res.json();
  }

  function debounce(fn, ms) {
    let t;
    return function() { clearTimeout(t); const args = arguments; t = setTimeout(() => fn.apply(null, args), ms); };
  }

  function esc(s) { return String(s || '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c])); }

  // ── <quick-order-search> ──────────────────────────────────────────────────
  class QuickOrderSearch extends HTMLElement {
    constructor() {
      super();
      this.draft = new Map();
      this.activeIndex = -1;
    }

    connectedCallback() {
      this.input = this.querySelector('.bx-quick__search-input');
      this.results = this.querySelector('.bx-quick__search-results');

      const root = this.closest('.bx-quick');
      this.list = root.querySelector('[data-quick-order-list]');
      this.countEl = root.querySelector('[data-quick-order-count]');
      this.subEl = root.querySelector('[data-quick-order-subtotal]');
      this.submitBtn = root.querySelector('[data-quick-order-submit]');
      this.clearBtn = root.querySelector('[data-quick-order-clear]');
      this.pasteBtn = root.querySelector('[data-quick-order-paste-validate]');
      this.pasteInput = root.querySelector('.bx-quick__paste-input');

      this.input.addEventListener('input', debounce(this._onSearch.bind(this), 200));
      this.input.addEventListener('keydown', this._onKey.bind(this));
      this.input.addEventListener('blur', () => setTimeout(() => this._hideResults(), 150));

      if (this.submitBtn) this.submitBtn.addEventListener('click', this._onSubmit.bind(this));
      if (this.clearBtn) this.clearBtn.addEventListener('click', () => { this.draft.clear(); this._renderDraft(); });
      if (this.pasteBtn) this.pasteBtn.addEventListener('click', this._onPaste.bind(this));
    }

    async _onSearch() {
      const q = this.input.value.trim();
      if (q.length < 2) { this._hideResults(); return; }
      const products = await searchProducts(q, 6);
      this._renderResults(products, q);
    }

    _onKey(e) {
      const items = this.results.querySelectorAll('.bx-quick__result');
      if (e.key === 'ArrowDown') { e.preventDefault(); this._move(1, items); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this._move(-1, items); }
      else if (e.key === 'Enter' && this.activeIndex >= 0 && items[this.activeIndex]) { e.preventDefault(); items[this.activeIndex].click(); }
      else if (e.key === 'Escape') { this.input.value = ''; this._hideResults(); }
    }

    _move(delta, items) {
      if (!items.length) return;
      this.activeIndex = (this.activeIndex + delta + items.length) % items.length;
      items.forEach((el, i) => el.classList.toggle('is-active', i === this.activeIndex));
    }

    _hideResults() { this.results.innerHTML = ''; this.activeIndex = -1; }

    _renderResults(products, q) {
      if (!products.length) { this.results.innerHTML = '<div class="bx-quick__no-results">No matches for "' + esc(q) + '"</div>'; return; }
      const isTrade = window.bouchon && window.bouchon.isTrade;
      this.results.innerHTML = products.map(p => {
        const variant = (p.variants && p.variants[0]) || null;
        const displayCents = isTrade ? priceExGstCents(p.price, p, variant) : p.price;
        const priceLabel = isTrade
          ? `${formatMoney(displayCents)} <span class="bx-quick__result-exgst">ex GST</span>`
          : formatMoney(displayCents);
        return `
          <button type="button" class="bx-quick__result" data-handle="${p.handle}" role="option">
            <span class="bx-quick__result-title">${esc(p.title)}</span>
            ${p.vendor ? `<span class="bx-quick__result-vendor">${esc(p.vendor)}</span>` : ''}
            <span class="bx-quick__result-price">${priceLabel}</span>
          </button>`;
      }).join('');
      this.results.querySelectorAll('.bx-quick__result').forEach(btn => {
        btn.addEventListener('click', () => this._addToDraft(btn.dataset.handle));
      });
      this.activeIndex = 0;
      const first = this.results.querySelector('.bx-quick__result');
      if (first) first.classList.add('is-active');
    }

    async _addToDraft(handle) {
      this.input.value = '';
      this._hideResults();
      if (this.draft.has(handle)) {
        const entry = this.draft.get(handle);
        entry.qty = (entry.qty || 0) + 1;
        this._renderDraft();
        return;
      }
      const product = await fetchProductByHandle(handle);
      if (!product) return;
      this.draft.set(handle, { product: product, qty: 1 });
      this._renderDraft();
    }

    _renderDraft() {
      if (this.draft.size === 0) {
        this.list.innerHTML = '<div class="bx-quick__draft-empty">Add products from the left to build your order.</div>';
        if (this.countEl) this.countEl.textContent = '0';
        if (this.subEl) this.subEl.textContent = '$0.00';
        if (this.submitBtn) this.submitBtn.disabled = true;
        return;
      }
      let subtotalCents = 0;
      let totalUnits = 0;
      const rows = [];
      for (const [handle, entry] of this.draft.entries()) {
        const variant = (entry.product.variants && entry.product.variants[0]) || {};
        const lineCents = (variant.price || 0) * entry.qty;
        subtotalCents += priceExGstCents(lineCents, entry.product, variant);
        totalUnits += entry.qty;
        rows.push({ handle: handle, entry: entry, variant: variant, lineCents: lineCents });
      }
      this.list.innerHTML = rows.map(({ handle, entry, variant, lineCents }) => `
        <div class="bx-quick__draft-row" data-handle="${handle}">
          <div class="bx-quick__draft-meta">
            <div class="bx-quick__draft-vendor">${esc(entry.product.vendor || '')}</div>
            <div class="bx-quick__draft-title">${esc(entry.product.title)}</div>
            <div class="bx-quick__draft-sku">${esc(variant.sku || handle)}</div>
          </div>
          <div class="bx-quick__draft-qty">
            <button type="button" data-qty-down aria-label="Decrease">–</button>
            <input type="number" min="1" value="${entry.qty}" data-qty-input>
            <button type="button" data-qty-up aria-label="Increase">+</button>
          </div>
          <div class="bx-quick__draft-line">${formatMoney(priceExGstCents(lineCents, entry.product, variant))}</div>
          <button type="button" class="bx-quick__draft-remove" data-remove aria-label="Remove">×</button>
        </div>
      `).join('');
      this.countEl.textContent = totalUnits;
      this.subEl.textContent = formatMoney(subtotalCents);
      this.submitBtn.disabled = false;

      this.list.querySelectorAll('.bx-quick__draft-row').forEach(row => {
        const handle = row.dataset.handle;
        row.querySelector('[data-qty-up]').addEventListener('click', () => this._adjust(handle, +1));
        row.querySelector('[data-qty-down]').addEventListener('click', () => this._adjust(handle, -1));
        row.querySelector('[data-qty-input]').addEventListener('change', (e) => this._setQty(handle, parseInt(e.target.value, 10) || 1));
        row.querySelector('[data-remove]').addEventListener('click', () => { this.draft.delete(handle); this._renderDraft(); });
      });
    }

    _adjust(handle, delta) {
      const entry = this.draft.get(handle);
      if (!entry) return;
      entry.qty = Math.max(1, entry.qty + delta);
      this._renderDraft();
    }
    _setQty(handle, qty) {
      const entry = this.draft.get(handle);
      if (!entry) return;
      entry.qty = Math.max(1, qty);
      this._renderDraft();
    }

    async _onSubmit() {
      if (this.draft.size === 0) return;
      this.submitBtn.disabled = true;
      this.submitBtn.textContent = 'Adding…';
      const items = [];
      for (const entry of this.draft.values()) {
        const variant = (entry.product.variants && entry.product.variants[0]);
        if (variant) items.push({ id: variant.id, quantity: entry.qty });
      }
      try {
        await cartAddItems(items);
        this.draft.clear();
        window.location.href = window.routes.cartUrl || '/cart';
      } catch (err) {
        this.submitBtn.disabled = false;
        this.submitBtn.textContent = 'Add to cart →';
        alert('Could not add to cart: ' + err.message);
      }
    }

    async _onPaste() {
      const text = (this.pasteInput.value || '').trim();
      if (!text) return;
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      this.pasteBtn.disabled = true;
      for (const line of lines) {
        const parts = line.split(/[,\t]/).map(p => p.trim());
        const sku = parts[0];
        const qty = Math.max(1, parseInt(parts[1] || '1', 10) || 1);
        if (!sku) continue;
        const matches = await searchProducts(sku, 3);
        const product = matches.find(p => (p.variants || []).some(v => (v.sku || '').toLowerCase() === sku.toLowerCase())) || matches[0];
        if (product) {
          const full = await fetchProductByHandle(product.handle);
          if (full) this.draft.set(full.handle, { product: full, qty: qty });
        }
      }
      this.pasteBtn.disabled = false;
      this.pasteInput.value = '';
      this._renderDraft();
    }
  }

  if (!customElements.get('quick-order-search')) {
    customElements.define('quick-order-search', QuickOrderSearch);
  }

  // ── <sample-picker> ────────────────────────────────────────────────────────
  class SamplePicker extends HTMLElement {
    constructor() { super(); this.selections = new Map(); }

    connectedCallback() {
      this.input = this.querySelector('.bx-samples__picker-input');
      this.results = this.querySelector('.bx-samples__picker-results');
      const root = this.closest('.bx-samples');
      this.list = root.querySelector('[data-samples-list]');
      this.payload = root.querySelector('[data-samples-payload]');

      this.input.addEventListener('input', debounce(this._onSearch.bind(this), 200));
      this.input.addEventListener('blur', () => setTimeout(() => this._hide(), 150));
    }

    async _onSearch() {
      const q = this.input.value.trim();
      if (q.length < 2) { this._hide(); return; }
      const products = await searchProducts(q, 6);
      if (!products.length) { this.results.innerHTML = '<div class="bx-samples__no-results">No matches.</div>'; return; }
      this.results.innerHTML = products.map(p => `
        <button type="button" class="bx-samples__picker-result" data-handle="${p.handle}" data-title="${esc(p.title)}">
          <span>${esc(p.title)}</span>
          ${p.vendor ? `<span class="bx-samples__picker-vendor">${esc(p.vendor)}</span>` : ''}
        </button>
      `).join('');
      this.results.querySelectorAll('.bx-samples__picker-result').forEach(btn => {
        btn.addEventListener('click', () => this._add(btn.dataset.handle, btn.dataset.title));
      });
    }

    _hide() { this.results.innerHTML = ''; }

    _add(handle, title) {
      this.input.value = '';
      this._hide();
      if (this.selections.has(handle)) return;
      this.selections.set(handle, { handle: handle, title: title });
      this._render();
    }

    _render() {
      if (this.selections.size === 0) {
        this.list.innerHTML = '<li class="bx-samples__list-empty">No products yet — search above.</li>';
        this.payload.value = '';
        return;
      }
      this.list.innerHTML = '';
      const lines = [];
      for (const item of this.selections.values()) {
        lines.push('• ' + item.title);
        const li = document.createElement('li');
        li.className = 'bx-samples__list-item';
        li.innerHTML = '<span>' + esc(item.title) + '</span><button type="button" aria-label="Remove">×</button>';
        li.querySelector('button').addEventListener('click', () => { this.selections.delete(item.handle); this._render(); });
        this.list.appendChild(li);
      }
      this.payload.value = 'Sample request — products:\n\n' + lines.join('\n') + '\n\nSubmitted via /pages/sample-request';
    }
  }

  if (!customElements.get('sample-picker')) {
    customElements.define('sample-picker', SamplePicker);
  }

  // ── <csv-upload> ──────────────────────────────────────────────────────────
  class CsvUpload extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-csv-input]');
      this.dropEl = this.querySelector('.bx-csv__drop');
      this.previewBody = this.querySelector('[data-csv-preview] tbody');
      this.confirmBtn = this.querySelector('[data-csv-confirm]');
      this.confirmCount = this.querySelector('[data-csv-confirm-count]');
      this.statOk = this.querySelector('[data-csv-ok]');
      this.statWarn = this.querySelector('[data-csv-warn]');
      this.statErr = this.querySelector('[data-csv-err]');
      this.addedEl = this.querySelector('[data-csv-added]');
      this.parsedRows = [];

      this.input.addEventListener('change', e => this._handleFile(e.target.files[0]));
      this.dropEl.addEventListener('dragover', e => { e.preventDefault(); this.dropEl.classList.add('is-drag'); });
      this.dropEl.addEventListener('dragleave', () => this.dropEl.classList.remove('is-drag'));
      this.dropEl.addEventListener('drop', e => {
        e.preventDefault();
        this.dropEl.classList.remove('is-drag');
        if (e.dataTransfer.files[0]) this._handleFile(e.dataTransfer.files[0]);
      });

      this.querySelectorAll('[data-csv-restart]').forEach(btn => btn.addEventListener('click', e => { e.preventDefault(); this._showPanel('upload'); this.parsedRows = []; this.input.value = ''; }));
      this.confirmBtn.addEventListener('click', this._confirm.bind(this));
    }

    async _handleFile(file) {
      if (!file) return;
      const text = await file.text();
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      const startIdx = /^\s*sku/i.test(lines[0]) ? 1 : 0;
      this.parsedRows = [];
      for (let i = startIdx; i < lines.length; i++) {
        const parts = lines[i].split(/[,\t]/).map(p => p.trim());
        const sku = parts[0];
        const qty = Math.max(1, parseInt(parts[1] || '1', 10) || 1);
        if (!sku) continue;
        this.parsedRows.push({ sku: sku, qty: qty, status: 'pending', product: null });
      }
      this._showPanel('review');
      this._renderTableSkeleton();
      for (const row of this.parsedRows) {
        // Resolve in 3 stages:
        //   1) Direct handle lookup (`/products/<handle>.js`) — predictive
        //      search doesn't match handles, but lots of input here IS a
        //      handle (cer-arcaextraroma etc.), so try that first.
        //   2) Predictive search — fuzzy title/SKU match for "massenez peach"
        //      style entries. Prefer exact variant-SKU match; else top hit.
        //   3) Fail.
        let full = null;
        const looksLikeHandle = /^[a-z0-9][a-z0-9-]*$/i.test(row.sku);
        if (looksLikeHandle) {
          full = await fetchProductByHandle(row.sku.toLowerCase());
        }
        if (!full) {
          const matches = await searchProducts(row.sku, 3);
          const product = matches.find(p => (p.variants || []).some(v => (v.sku || '').toLowerCase() === row.sku.toLowerCase())) || matches[0];
          if (product) full = await fetchProductByHandle(product.handle);
        }
        if (full) {
          row.product = full;
          row.status = full.available ? 'ok' : 'warn';
        } else {
          row.status = 'error';
        }
        this._renderTable();
      }
      this._renderStats();
    }

    _renderTableSkeleton() {
      this.previewBody.innerHTML = this.parsedRows.map((r, i) =>
        '<tr data-idx="' + i + '"><td><span class="bx-csv__cell-status bx-csv__cell-status--pending">…</span></td><td>' + esc(r.sku) + '</td><td><span class="bx-csv__cell-loading">resolving…</span></td><td>' + r.qty + '</td><td>—</td></tr>'
      ).join('');
    }

    _renderTable() {
      this.previewBody.innerHTML = this.parsedRows.map((r, i) => {
        const variant = r.product && r.product.variants && r.product.variants[0];
        const lineCents = variant ? priceExGstCents(variant.price * r.qty, r.product, variant) : 0;
        const statusLabel = { ok: 'OK', warn: 'Backorder', error: 'Not found', pending: '…' }[r.status];
        return '<tr data-idx="' + i + '" class="bx-csv__row bx-csv__row--' + r.status + '"><td><span class="bx-csv__cell-status bx-csv__cell-status--' + r.status + '">' + statusLabel + '</span></td><td class="bx-csv__cell-mono">' + esc(r.sku) + '</td><td>' + (r.product ? esc(r.product.title) : '<em>not found</em>') + '</td><td>' + r.qty + '</td><td>' + (variant ? formatMoney(lineCents) : '—') + '</td></tr>';
      }).join('');
    }

    _renderStats() {
      const ok = this.parsedRows.filter(r => r.status === 'ok').length;
      const warn = this.parsedRows.filter(r => r.status === 'warn').length;
      const err = this.parsedRows.filter(r => r.status === 'error').length;
      this.statOk.textContent = ok;
      this.statWarn.textContent = warn;
      this.statErr.textContent = err;
      const validCount = ok + warn;
      this.confirmCount.textContent = validCount;
      this.confirmBtn.disabled = validCount === 0;
    }

    async _confirm() {
      const valid = this.parsedRows.filter(r => r.product && r.product.variants && r.product.variants[0] && r.status !== 'error');
      const items = valid.map(r => ({ id: r.product.variants[0].id, quantity: r.qty }));
      this.confirmBtn.disabled = true;
      try {
        await cartAddItems(items);
        this.addedEl.textContent = items.reduce((sum, i) => sum + i.quantity, 0);
        this._showPanel('confirm');
      } catch (err) {
        this.confirmBtn.disabled = false;
        alert('Could not add to cart: ' + err.message);
      }
    }

    _showPanel(name) {
      this.querySelectorAll('.bx-csv__panel').forEach(p => p.hidden = p.dataset.panel !== name);
      this.querySelectorAll('.bx-csv__step').forEach(s => s.classList.toggle('is-active', s.dataset.step === name));
    }
  }

  if (!customElements.get('csv-upload')) {
    customElements.define('csv-upload', CsvUpload);
  }
})();
